import { type GetCompliancePositionsListQuery } from "~/graphql/supergraphTypes";
import { formatShortFormDate } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import { getRiskRating } from "../../riskRating/utils";
import { ProgramName } from "../../utils/constants";
import { PositionsTableRowItem } from "../types";

const formatPositionName = (
  accountName: string | undefined,
  entityName: string | undefined,
  portalStatus: ReadonlyArray<string | null> | null | undefined,
) => {
  if (!accountName && !entityName) return null;

  const sanitizedPortalStatus =
    portalStatus?.filter((status): status is string => status !== null) ?? null;

  return {
    accountName,
    entityName,
    portalStatus: sanitizedPortalStatus,
  };
};

export const rowsFromQuery = (
  data: GetCompliancePositionsListQuery,
): PositionsTableRowItem[] => {
  const edges = data?.positions?.edges ?? [];
  const rowData: PositionsTableRowItem[] = edges.map((edge) => {
    const node = edge?.node;
    const riskRating = getRiskRating(
      node.onboardingCase?.latestInvestingProfile?.riskRating,
      node.onboardingCase?.programId,
    );

    return {
      id: toDbId(node.id),
      position: formatPositionName(
        node.account?.name,
        node.investmentEntity?.name,
        node.investmentEntity?.portalStatus ?? null,
      ),
      status: node.status ?? null,
      investingAccount: node.account?.name ?? null,
      entity: {
        id: node.investmentEntity?.id ?? "",
        name: node.investmentEntity?.name ?? "",
      },
      client: node.arena
        ? {
            id: node.arena.id,
            name: node.arena.name,
          }
        : null,
      program: node.complianceProgram as (ProgramName | null)[] | null,
      date: formatShortFormDate(node.createdAt),
      riskRating,
      isLegacyKyc: node.isLegacyKyc ?? false,
      onboardingStatus: node.onboardingCase
        ? {
            id: node.onboardingCase.id,
            name: node.onboardingCase.name,
            status: node.onboardingCase.status,
            caseType: node.onboardingCase.caseType,
            subStatus: node.onboardingCase.subStatus,
            investingProfile: node.onboardingCase.investingProfile,
            latestInvestingProfile: node.onboardingCase.latestInvestingProfile,
            externalObjectId: node.onboardingCase.externalObject?.objectId,
            complianceInvitation: node.onboardingCase.complianceInvitation,
          }
        : null,
      prospectId: node.prospect?.id ?? null,
      subscriptionId: node.subscription?.id ?? null,
      activeCases: node.activeCases ?? null,
    };
  });

  return rowData;
};
